import React, { useState, useEffect, useRef } from 'react';
import { Helmet } from 'react-helmet-async';
import { Eye, EyeOff, Lock, User, LogIn } from 'lucide-react';
import { useNavigate, Link } from 'react-router-dom';
import { AdminApi } from '../../../../api/services/admin/adminapi';
import logoColor from '../../../../assets/img/logo-color.png';
import './login.css';

const AdminLogin = () => {
  const [credentials, setCredentials] = useState({
    username: '',
    password: ''
  });
  const [isLoading, setIsLoading] = useState(false);
  const [loginAttempts, setLoginAttempts] = useState(() => {
    const attempts = parseInt(localStorage.getItem('loginAttempts') || '0');
    const lockoutTime = localStorage.getItem('loginLockoutTime');
    
    if (lockoutTime && attempts >= 5) {
      const now = Date.now();
      const lockoutTimestamp = parseInt(lockoutTime);
      if (now - lockoutTimestamp >= 60000) {
        localStorage.removeItem('loginAttempts');
        localStorage.removeItem('loginLockoutTime');
        return 0;
      }
    }
    
    return attempts;
  });
  const [showPassword, setShowPassword] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const formRef = useRef(null);
  const navigate = useNavigate();

  useEffect(() => {
    const timer = setInterval(() => {
      const lockoutTime = localStorage.getItem('loginLockoutTime');
      const attempts = parseInt(localStorage.getItem('loginAttempts') || '0');
      
      if (lockoutTime && attempts >= 5) {
        const now = Date.now();
        const lockoutTimestamp = parseInt(lockoutTime);
        if (now - lockoutTimestamp >= 60000) {
          localStorage.removeItem('loginAttempts');
          localStorage.removeItem('loginLockoutTime');
          setLoginAttempts(0);
          setErrorMessage('');
        }
      }
    }, 1000);

    return () => clearInterval(timer);
  }, []);

  // 에러 메시지 표시 및 자동 숨김
  const showError = (message) => {
    setErrorMessage(message);
    setTimeout(() => setErrorMessage(''), 5000); // 5초 후 자동 숨김
  };

  // Enter 키 핸들러
  useEffect(() => {
    const handleKeyPress = (e) => {
      if (e.key === 'Enter' && !isLoading) {
        const form = formRef.current;
        if (form) {
          form.requestSubmit();
        }
      }
    };

    document.addEventListener('keydown', handleKeyPress);
    return () => document.removeEventListener('keydown', handleKeyPress);
  }, [isLoading]);

  const handleLogin = async (e) => {
    e.preventDefault();
    setErrorMessage(''); // 에러 메시지 초기화
    
    // 로그인 시도 횟수 체크 (5회 초과 시 차단)
    if (loginAttempts >= 5) {
      showError('로그인 시도 횟수가 초과되었습니다. 1분 후 다시 시도해주세요.');
      return;
    }
    
    // 입력 검증 강화
    const trimmedUsername = credentials.username.trim();
    const trimmedPassword = credentials.password.trim();
    
    if (!trimmedUsername || !trimmedPassword) {
      showError('아이디와 비밀번호를 입력해주세요.');
      return;
    }

    if (trimmedUsername.length < 3) {
      showError('아이디는 3자 이상 입력해주세요.');
      return;
    }

    setIsLoading(true);

    try {
      const data = await AdminApi.login({
        username: trimmedUsername,
        password: trimmedPassword
      });
      
      if (data.success && data.data && data.data.accessToken) {
        localStorage.setItem('adminToken', data.data.accessToken);
        localStorage.removeItem('loginAttempts'); // 성공 시 초기화
        localStorage.removeItem('loginLockoutTime'); // 잠금 타임스탬프도 초기화
        setLoginAttempts(0);
        
        // 마지막 로그인 시간 저장 (응답에서 받아옴)
        if (data.data.admin?.lastLoginAt) {
          localStorage.setItem('lastLoginTime', data.data.admin.lastLoginAt);
        }
        
        navigate('/admin', { replace: true });
      } else {
        showError('로그인에 실패했습니다. 다시 시도해주세요.');
      }
    } catch (error) {
      console.error('Login error:', error);
      
      // 에러 응답 처리 개선
      let errorMessage = '로그인 중 오류가 발생했습니다.';
      
      // 타임아웃 에러 처리
      if (error.code === 'ECONNABORTED' || error.message?.includes('timeout')) {
        errorMessage = '서버 응답 시간이 초과되었습니다. 네트워크 연결을 확인하거나 잠시 후 다시 시도해주세요.';
      } else if (error.response) {
        const status = error.response.status;
        const message = error.response.data?.message || error.response.data?.error;
        
        if (status === 429) { // Too Many Requests
          errorMessage = message || '로그인 시도 횟수가 초과되었습니다. 잠시 후 다시 시도해주세요.';
        } else if (status === 400) {
          errorMessage = message || '아이디 또는 비밀번호가 일치하지 않습니다.';
        } else if (status === 403) {
          errorMessage = '사용중지된 계정입니다. 관리자에게 문의하세요.';
        } else {
          errorMessage = message || errorMessage;
        }
      } else if (error.request) {
        errorMessage = '서버에 연결할 수 없습니다. 네트워크를 확인해주세요.';
      }
      
      showError(errorMessage);
      
      // 실패 시 로그인 시도 횟수 증가 (로컬 저장)
      const attempts = loginAttempts + 1;
      localStorage.setItem('loginAttempts', attempts.toString());
      setLoginAttempts(attempts);
      
      if (attempts >= 5) {
        // 잠금 타임스탬프 저장
        localStorage.setItem('loginLockoutTime', Date.now().toString());
        showError('로그인 시도 횟수가 초과되었습니다. 1분 후 다시 시도해주세요.');
      }
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="gakkaun-admin-login-page">
      <Helmet>
        <title>주식회사 가까운물류</title>
      </Helmet>
      <div className="gakkaun-admin-login-container">
        {/* 로고 */}
        <div className="gakkaun-admin-login-logo">
          <Link to="/" className="gakkaun-admin-login-logo-link">
            <img src={logoColor} alt="로고" className="gakkaun-admin-login-logo-img" />
          </Link>
        </div>

        {/* 로그인 카드 */}
        <div className="gakkaun-admin-login-card">
          <h1 className="gakkaun-admin-login-title">로그인</h1>

          {/* 에러 메시지 */}
          {errorMessage && (
            <div className="gakkaun-admin-login-error">
              <span className="gakkaun-admin-login-error-icon">⚠️</span>
              <span className="gakkaun-admin-login-error-text">{errorMessage}</span>
            </div>
          )}

          {/* 로그인 폼 */}
          <form 
            ref={formRef}
            onSubmit={handleLogin} 
            className="gakkaun-admin-login-form"
            autoComplete="off"
          >
            <div className="gakkaun-admin-login-input-group">
              <label className="gakkaun-admin-login-label">아이디</label>
              <div className="gakkaun-admin-login-input-wrapper">
                <User size={18} className="gakkaun-admin-login-input-icon" />
                <input
                  type="text"
                  placeholder="아이디를 입력하세요"
                  value={credentials.username}
                  onChange={(e) => setCredentials({...credentials, username: e.target.value})}
                  className="gakkaun-admin-login-input"
                  disabled={isLoading}
                  autoComplete="off"
                  autoFocus
                />
              </div>
            </div>

            <div className="gakkaun-admin-login-input-group">
              <label className="gakkaun-admin-login-label">비밀번호</label>
              <div className="gakkaun-admin-login-input-wrapper">
                <Lock size={18} className="gakkaun-admin-login-input-icon" />
                <input
                  type={showPassword ? "text" : "password"}
                  placeholder="비밀번호를 입력하세요"
                  value={credentials.password}
                  onChange={(e) => setCredentials({...credentials, password: e.target.value})}
                  className="gakkaun-admin-login-input"
                  disabled={isLoading}
                  autoComplete="off"
                />
                <button
                  type="button"
                  className="gakkaun-admin-login-password-toggle"
                  onClick={() => setShowPassword(!showPassword)}
                  disabled={isLoading}
                  tabIndex={-1}
                >
                  {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                </button>
              </div>
            </div>
            
            <button 
              type="submit" 
              className="gakkaun-admin-login-button" 
              disabled={isLoading || loginAttempts >= 5}
            >
              {isLoading ? (
                <>
                  <div className="gakkaun-admin-login-spinner"></div>
                  로그인 중...
                </>
              ) : loginAttempts >= 5 ? (
                '로그인 차단됨'
              ) : (
                '로그인'
              )}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
};

export default AdminLogin;